// src/pages/returnToVendor/ReturnToVendorDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, RotateCcw, Package, PackageCheck, Receipt, Printer, XCircle, FileText, Building2, Calendar, User } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getReturnToVendor, voidReturnToVendor } from '../../services/api/procurement/returnToVendor';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { exportReturnToVendorPDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import { PURCHASE_ORDER_BASE_PATH } from '../../utils/procurementNavigation';
import ReturnToVendorA4Document from './ReturnToVendorA4Document';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './ReturnToVendorDetailsPage.module.css';

const STATUS_COLORS = {
  completed: 'success',
  voided: 'error',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const itemName = (item) => {
  const product = item.Product;
  const variation = item.ProductVariation;
  if (!product) return `Item #${item.grnItemId}`;
  return `${product.name}${variation?.sku ? ` — ${variation.sku}` : ''}`;
};

const ReturnToVendorDetailsPage = () => {
  const { rtvId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [rtv, setRtv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrint, setShowPrint] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState(false);

  const loadRtv = async () => {
    try {
      setLoading(true);
      const result = await getReturnToVendor(rtvId);
      setRtv(result.returnToVendor);
    } catch (err) {
      error(err?.response?.data?.message || t('returnToVendor.failedToLoadReturnToVendor'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRtv(); }, [rtvId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => setShowPrint(true);

  const handleVoid = async () => {
    setIsVoiding(true);
    try {
      await voidReturnToVendor(rtvId, { voidReason: voidReason.trim() || undefined });
      success(t('returnToVendor.rtvVoidedSuccessfully'));
      setShowVoidModal(false);
      setVoidReason('');
      loadRtv();
    } catch (err) {
      error(err?.response?.data?.message || t('returnToVendor.failedToVoidReturnToVendor'));
    } finally {
      setIsVoiding(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!rtv) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const renderContent = () => (
    <div className={styles.grid}>
      <div className={styles.column}>
        <InfoCard icon={RotateCcw} title={t('returnToVendor.rtvNumber')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('common.status')}</span>
              <span className={ps.infoValue}>
                <Badge type={STATUS_COLORS[rtv.status]} label={t(`returnToVendor.status_${rtv.status}`)} />
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('returnToVendor.purchaseOrderNumber')}</span>
              <span className={ps.infoValue}>
                <FileText size={14} />
                {rtv.purchaseOrder?.purchaseOrderNumber ? (
                  <Link to={`${PURCHASE_ORDER_BASE_PATH}/${rtv.purchaseOrder.id}`}>{rtv.purchaseOrder.purchaseOrderNumber}</Link>
                ) : t('common.na')}
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('returnToVendor.supplier')}</span>
              <span className={ps.infoValue}><Building2 size={14} />{rtv.supplier?.name || rtv.Supplier?.name || t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('returnToVendor.grnNumber')}</span>
              <span className={ps.infoValue}>
                <PackageCheck size={14} />
                {rtv.goodsReceivedNote?.grnNumber ? (
                  <Link to={`/procurement/grn/${rtv.goodsReceivedNote.id}`}>{rtv.goodsReceivedNote.grnNumber}</Link>
                ) : t('common.na')}
              </span>
            </div>
            <div className={ps.divider} />
            <div className={`${ps.infoItem} ${ps.fullWidth}`}>
              <span className={ps.infoLabel}>{t('returnToVendor.totalDebitAmount')}</span>
              <span className={`${ps.infoValue} ${ps.infoValueLarge} ${ps.infoValueDanger}`}>{formatCurrency(rtv.totalDebitAmount)}</span>
            </div>
            {rtv.reason && (
              <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                <span className={ps.infoLabel}>{t('returnToVendor.reason')}</span>
                <p className={ps.notesText}>{rtv.reason}</p>
              </div>
            )}
          </div>
        </InfoCard>

        <InfoCard icon={Receipt} title={t('returnToVendor.linkedDebitNotes')}>
          {(rtv.debitNotes || []).length === 0 ? (
            <p className={ps.infoValueMuted}>{t('common.na')}</p>
          ) : (
            <div className={ps.tableWrapper}>
              <table className={ps.itemsTable}>
                <tbody>
                  {rtv.debitNotes.map((dn) => (
                    <tr key={dn.id}>
                      <td><Link className={ps.docLink} to={`/procurement/debit-notes/${dn.id}`}>{dn.debitNoteNumber}</Link></td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(dn.amount)}</td>
                      <td className={ps.alignRight}>
                        <Badge
                          type={dn.status === 'cancelled' ? 'error' : dn.status === 'applied' ? 'success' : 'info'}
                          label={t(`debitNote.status_${dn.status}`)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </InfoCard>
      </div>

      <div className={styles.column}>
        <InfoCard icon={Package} title={t('returnToVendor.itemsReturned')}>
          <div className={ps.tableWrapper}>
            <table className={ps.itemsTable}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th>{t('returnToVendor.item')}</th>
                  <th>{t('returnToVendor.warehouse')}</th>
                  <th className={ps.alignRight}>{t('returnToVendor.quantityReturned')}</th>
                  <th className={ps.alignRight}>{t('returnToVendor.unitPriceLabel')}</th>
                  <th className={ps.alignRight}>{t('returnToVendor.lineAmount')}</th>
                </tr>
              </thead>
              <tbody>
                {(rtv.items || []).map((item, index) => {
                  const sku = item.ProductVariation?.sku || item.Product?.sku;
                  return (
                    <tr key={item.id}>
                      <td className={ps.itemIndexCell}>{index + 1}</td>
                      <td>
                        <div className={ps.productCellInner}>
                          <ProductThumb item={item} alt={item.Product?.name || ''} />
                          <div className={ps.productCell}>
                            <span className={ps.productName}>{item.Product?.name || itemName(item)}</span>
                            {sku && <span className={ps.productSku}>SKU: {sku}</span>}
                          </div>
                        </div>
                      </td>
                      <td>{item.returnFromWarehouse?.name || t('common.na')}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{item.quantityReturned}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(item.unitPrice)}</td>
                      <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(item.lineAmount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </InfoCard>

        <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
          <div className={ps.infoList}>
            <div className={ps.infoListItem}>
              <label>{t('returnToVendor.createdBy')}</label>
              <p><User size={14} />{rtv.createdBy?.name || t('common.na')}</p>
            </div>
            <div className={ps.infoListItem}>
              <label>{t('common.created')}</label>
              <p><Calendar size={14} />{formatDateTime(rtv.createdAt)}</p>
            </div>
          </div>
        </InfoCard>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        className={styles.detailHeader}
        title={rtv.rtvNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/procurement/return-to-vendor')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('returnToVendor.allReturnToVendors')}
          </button>
        }
        actions={
          <div className={styles.headerActions}>
            {rtv.status === 'completed' && hasPermission('void_rtv') && (
              <button className={`${styles.secondaryBtn} ${ps.toneDanger}`} onClick={() => setShowVoidModal(true)}>
                <XCircle size={15} /> {t('returnToVendor.void')}
              </button>
            )}
            <button className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
          </div>
        }
      />

      {renderContent()}

      {showVoidModal && (
        <div className={styles.overlay}>
          <div className={styles.smallModal}>
            <div className={styles.modalHeader}>
              <h3>{t('returnToVendor.voidReturnToVendor')}</h3>
            </div>
            <div className={styles.modalBody}>
              <p>{t('returnToVendor.confirmVoid')}</p>
              <label>{t('returnToVendor.voidReason')}</label>
              <textarea
                className={styles.select}
                rows={3}
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('returnToVendor.voidReasonPlaceholder')}
              />
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowVoidModal(false)} disabled={isVoiding}>
                {t('common.cancel')}
              </button>
              <button className={styles.primaryBtn} onClick={handleVoid} disabled={isVoiding}>
                {isVoiding ? t('common.loading') : t('returnToVendor.voidReturnToVendor')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrint && (
        <PrintPreviewModal
          title={`Return To Vendor — ${rtv.rtvNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Return_To_Vendor_${rtv.rtvNumber || rtv.id}.pdf`}
          onDownloadPdf={() => exportReturnToVendorPDF(rtv, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderA4={() => <ReturnToVendorA4Document rtv={rtv} />}
        />
      )}
    </div>
  );
};

export default ReturnToVendorDetailsPage;
